import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Ticket, Calendar, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { getBackendUrl } from '../../../services/origin';

const API = getBackendUrl();

/**
 * "I miei ordini" tab — lists paid event-ticket orders for the
 * authenticated customer (via dashboard token + PIN). Each order
 * expands to show one QR code per ticket with check-in status.
 */
export function OrdiniTab({ data, brand, tc, ts, token, pin }) {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState(null);
  const [openQR, setOpenQR] = useState(null);  // { orderToken, ticketIndex }

  useEffect(() => {
    if (!token) return;
    setOrders(null);
    setError(null);
    axios.get(`${API}/api/my/${token}/orders`, { params: pin ? { pin } : {} })
      .then(r => setOrders(r.data?.orders || []))
      .catch(err => setError(err.response?.data?.detail || 'Impossibile caricare gli ordini'));
  }, [token, pin]);

  if (orders === null && !error) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="orders-loading">
        <Loader2 className="animate-spin" size={24} style={{ color: brand.primary_color }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl p-4 mt-4 text-sm border" data-testid="orders-error"
        style={{ backgroundColor: `${brand.primary_color}10`, borderColor: `${brand.primary_color}30`, color: tc }}>
        {error}
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="text-center py-12" data-testid="orders-empty">
        <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3"
          style={{ backgroundColor: `${brand.primary_color}18` }}>
          <Ticket size={28} style={{ color: brand.primary_color }} />
        </div>
        <p className="text-base font-bold" style={{ color: tc }}>Nessun ordine</p>
        <p className="text-sm mt-1" style={{ color: ts }}>
          I biglietti che acquisti compariranno qui.
        </p>
      </div>
    );
  }

  const fmtEur = (n) => Number(n || 0).toFixed(2).replace('.', ',');
  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('it-IT', {
        day: '2-digit', month: 'short', year: 'numeric',
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-5 pb-4 pt-2" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.order_token} className="rounded-3xl p-6 border shadow-sm"
          style={{ backgroundColor: `${tc}06`, borderColor: `${tc}10` }}
          data-testid={`order-${o.order_token}`}>
          {/* Header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider"
                style={{ color: ts }}>
                <Calendar size={12} />
                <span>{fmtDate(o.paid_at)}</span>
              </div>
              <p className="text-lg font-bold mt-1.5 truncate" style={{ color: tc }}>
                {o.event_name}
              </p>
              <p className="text-sm mt-1" style={{ color: ts }}>
                {(o.lines || []).map(l => `${l.quantity}× ${l.tier_name}`).join(' · ')}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-base font-black tabular-nums" style={{ color: brand.primary_color }}>
                {fmtEur(o.total_eur)} €
              </p>
              <p className="text-xs uppercase tracking-wider mt-0.5" style={{ color: ts }}>
                pagato
              </p>
            </div>
          </div>

          {/* Tickets */}
          <div className="mt-4 space-y-2">
            {(o.tickets || []).map((t, i) => {
              const used = t.status === 'checked_in' || !!t.checked_in_at;
              return (
                <button
                  key={t.id}
                  onClick={() => setOpenQR({ orderToken: o.order_token, ticketIndex: i })}
                  className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 transition-all active:scale-[0.98] min-h-[56px]"
                  style={{
                    backgroundColor: `${brand.primary_color}${used ? '08' : '14'}`,
                    border: `1px solid ${brand.primary_color}${used ? '15' : '30'}`,
                    opacity: used ? 0.6 : 1,
                  }}
                  data-testid={`order-${o.order_token}-ticket-${i}`}
                >
                  <Ticket size={20} style={{ color: brand.primary_color }} />
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: tc }}>
                      {t.tier_name}
                    </p>
                    <p className="text-xs" style={{ color: ts }}>
                      Biglietto {i + 1} di {(o.tickets || []).length}
                    </p>
                  </div>
                  {used ? (
                    <span className="flex items-center gap-1 text-xs font-bold" style={{ color: ts }}>
                      <CheckCircle2 size={13} /> Usato
                    </span>
                  ) : (
                    <span className="text-xs font-bold uppercase tracking-wider"
                      style={{ color: brand.primary_color }}>
                      Mostra QR
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Fullscreen QR overlay */}
      {openQR && (() => {
        const order = orders.find(o => o.order_token === openQR.orderToken);
        const ticket = order?.tickets?.[openQR.ticketIndex];
        if (!ticket) return null;
        return (
          <div
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center p-6"
            style={{ backgroundColor: '#000000F5' }}
            onClick={() => setOpenQR(null)}
            data-testid="ticket-qr-modal"
          >
            <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 text-center">
                {order.event_name}
              </p>
              <p className="text-base font-bold text-center mt-1 text-slate-900">
                {ticket.tier_name}
              </p>
              <div className="bg-slate-50 rounded-2xl p-5 mt-4 flex justify-center">
                <QRCodeSVG value={ticket.qr_code} size={220} level="M" />
              </div>
              <p className="text-[10px] font-mono break-all mt-3 text-slate-400 text-center">
                {ticket.qr_code}
              </p>
              {ticket.checked_in_at ? (
                <div className="mt-4 flex items-center justify-center gap-1.5 text-xs font-bold text-amber-700">
                  <XCircle size={14} /> Già utilizzato
                </div>
              ) : (
                <div className="mt-4 flex items-center justify-center gap-1.5 text-xs font-bold text-emerald-700">
                  <CheckCircle2 size={14} /> Valido — mostra all'ingresso
                </div>
              )}
              <button
                onClick={() => setOpenQR(null)}
                className="mt-5 w-full rounded-2xl py-3 text-sm font-bold text-white"
                style={{ backgroundColor: brand.primary_color }}
                data-testid="close-qr-modal"
              >
                Chiudi
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
